import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  HStack,
  Text,
  useToast,
} from '@chakra-ui/react';
import type { MessageActions } from './MessageBubble';
import { messageActionItems } from './messageActionItems';
import { QuickReactions } from './QuickReactions';
import { replyLabel, type ChatMessage } from './messageUtils';
import { appLocale } from '../../lib/format';
import { useT } from '../../i18n';

interface MessageActionSheetProps {
  message: ChatMessage | null;
  currentUserId: string | undefined;
  isGroup: boolean;
  otherParticipantCount: number;
  actions: MessageActions;
  onClose: () => void;
}

// Press and hold a message (design: "Confirmations & downloads"): the chat
// dims, the message is lifted above it, and its actions come up from the
// bottom as a sheet with large rows.
export function MessageActionSheet({
  message,
  currentUserId,
  isGroup,
  otherParticipantCount,
  actions,
  onClose,
}: MessageActionSheetProps) {
  const toast = useToast();
  const t = useT();
  if (!message) return null;
  const isOwn = message.sender?.id === currentUserId;
  const deleted = Boolean(message.deletedAt);

  const items = messageActionItems({
    message,
    isOwn,
    isGroup,
    otherParticipantCount,
    actions,
    copyText: () => {
      navigator.clipboard
        .writeText(message.content)
        .then(() =>
          toast({
            title: t('chat.actions.copied'),
            status: 'success',
            duration: 1500,
          })
        )
        .catch(() =>
          toast({ title: t('chat.actions.couldNotCopy'), status: 'error' })
        );
    },
  });
  const run = (select: () => void) => {
    onClose();
    select();
  };

  return (
    <Drawer isOpen placement="bottom" onClose={onClose}>
      <DrawerOverlay bg="rgba(31, 29, 27, 0.55)" />
      {/* The held message, lifted above the dimmed chat. */}
      <Box
        position="fixed"
        top="max(env(safe-area-inset-top), 24px)"
        left={4}
        right={4}
        zIndex="modal"
        bg="surface"
        borderRadius="card"
        p="14px"
        boxShadow="0 16px 32px -12px rgba(0, 0, 0, 0.5)"
        aria-hidden="true"
        pointerEvents="none"
      >
        <HStack spacing={2} align="baseline" mb="4px">
          <Text fontWeight={700} fontSize="15px">
            {isOwn
              ? t('common.you')
              : (message.sender?.displayName ?? t('chat.unknown'))}
          </Text>
          <Text fontSize="13px" color="muted">
            {new Date(message.createdAt).toLocaleString(appLocale(), {
              weekday: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Text>
        </HStack>
        <Text fontSize="17px" lineHeight="25px" noOfLines={4}>
          {deleted ? t('chat.message.deleted') : replyLabel(message)}
        </Text>
      </Box>
      <DrawerContent
        borderTopRadius="28px"
        bg="surface"
        pb="max(env(safe-area-inset-bottom), 16px)"
        aria-label={t('chat.actions.label')}
      >
        <DrawerBody px={3} pt="10px" pb={0}>
          <Box
            w="40px"
            h="5px"
            borderRadius="3px"
            bg="gray.200"
            mx="auto"
            mb={2}
            aria-hidden="true"
          />
          {!deleted && message.type !== 'system' && (
            <QuickReactions
              onReact={(emoji) => run(() => actions.react(message, emoji))}
              onMore={() => run(() => actions.pickReaction?.(message))}
            />
          )}
          <Box role="menu" aria-label={t('chat.actions.label')}>
            {items.map((item) => (
              <Box
                key={item.key}
                as="button"
                type="button"
                role="menuitem"
                w="full"
                h="56px"
                px={3}
                borderRadius="16px"
                display="flex"
                alignItems="center"
                gap="14px"
                fontSize="17px"
                fontWeight={600}
                textAlign="left"
                color={item.danger ? 'danger' : 'ink'}
                _hover={{ bg: 'surface.subtle' }}
                _active={{ bg: 'surface.subtle' }}
                onClick={() => run(item.onSelect)}
              >
                <Box
                  color={item.danger ? 'danger' : 'accent'}
                  flexShrink={0}
                  aria-hidden="true"
                >
                  {item.icon}
                </Box>
                <Text as="span" flex="1">
                  {item.label}
                </Text>
                {item.detail && (
                  <Text
                    as="span"
                    fontSize="15px"
                    color="muted"
                    fontWeight={500}
                  >
                    {item.detail}
                  </Text>
                )}
              </Box>
            ))}
          </Box>
          <Button
            w="full"
            h="52px"
            mt={2}
            variant="outline"
            colorScheme="gray"
            borderColor="line"
            fontSize="17px"
            onClick={onClose}
          >
            {t('common.close')}
          </Button>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
